import { ApiResponse } from '@interfaces/index';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getToken } from './config';
import { apiUrlConfig } from './configs/apiUrlConfig';
import createApiClient from './configs/httpHandler';
import { createHeaders } from './httpHeaderConfig';

export const thoughtsAndSpeakUpsApiHandler = {
  getThoughtsAndSpeakUps: async (
    payload: Partial<GetThoughtsAndSpeakUpsPayload>,
  ): Promise<ApiResponse<ThoughtsAndSpeakUpsResponseInterface[]>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).post(
      '/get-user-thoughts-and-speak-ups',
      payload,
    ),

  markThoughtSeen: async (
    thoughtData: Partial<{ thoughtId: number }>,
  ): Promise<ApiResponse<ThoughtResponse>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).patch('/mark-thought-seen', thoughtData),

  getCompanySpeakUps: async (
    payload: GetCompanySpeakUpsPayload,
    page = 0,
    pageSize = 10,
  ): Promise<ApiResponse<CompanySpeakUpsResponse>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).post(
      `/get-company-speak-ups?page=${page}&pageSize=${pageSize}`,
      payload,
    ),

  getCompanyThoughts: async (
    payload: GetCompanyThoughtsPayload,
    page = 0,
    pageSize = 10,
  ): Promise<ApiResponse<CompanyThoughtsResponse>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).post(
      `/get-company-thoughts?page=${page}&pageSize=${pageSize}`,
      payload,
    ),
};

export const updateThoughtStatus = async (thoughtId = 0) => {
  const headers = createHeaders({ Authorization: `Bearer ${getToken()}` }) as Record<
    string,
    string
  >;

  delete headers['Content-Type'];
  await fetch(`${process.env.REACT_APP_API_URL}/responses/${thoughtId}`, {
    method: 'POST',
    headers: headers as HeadersInit,
  });
};

export const useMarkThoughtResponse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: thoughtsAndSpeakUpsApiHandler.markThoughtSeen,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['thoughtsAndSpeakUpsForNotifications'] });
    },
  });
};

export const useGetCompanySpeakUps = (
  payload: GetCompanySpeakUpsPayload,
  options?: {
    enabled?: boolean;
    refetchInterval?: number;
    page?: number;
    pageSize?: number;
  },
) =>
  useQuery({
    queryKey: ['companySpeakUps', payload, options?.page || 0, options?.pageSize || 10],
    queryFn: () =>
      thoughtsAndSpeakUpsApiHandler.getCompanySpeakUps(
        payload,
        options?.page || 0,
        options?.pageSize || 10,
      ),
    enabled: options?.enabled ?? true,
    refetchInterval: options?.refetchInterval,
  });

export const useGetCompanyThoughts = (
  payload: GetCompanyThoughtsPayload,
  options?: {
    enabled?: boolean;
    refetchInterval?: number;
    page?: number;
    pageSize?: number;
  },
) =>
  useQuery({
    queryKey: ['companyThoughts', payload, options?.page || 0, options?.pageSize || 10],
    queryFn: () =>
      thoughtsAndSpeakUpsApiHandler.getCompanyThoughts(
        payload,
        options?.page || 0,
        options?.pageSize || 10,
      ),
    enabled: options?.enabled ?? true,
    refetchInterval: options?.refetchInterval,
  });

export interface GetThoughtsAndSpeakUpsPayload {
  archived: boolean | null;
  type: string | null;
}

export interface ThoughtsAndSpeakUpsResponseInterface {
  id: number;
  type: 'thoughts' | 'speakUps';
  questionId: number | null;
  questionLabel: string | null;
  questionnaire: string | null;
  score: number | null;
  message: string;
  shared: boolean;
  latestResponse: {
    id: number;
    response: string;
    createdAt: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface ThoughtResponse {
  id: number;
  questionId: number | null;
  assessmentId: number | null;
  shareWith: string;
  thoughtsText: string;
  responded: boolean;
  responsesSeen: boolean;
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
  userAssessmentId: number;
  questionV2Id: number;
  userId: number;
}

export interface GetCompanySpeakUpsPayload {
  responded: boolean;
}

export interface SpeakUpTrigger {
  trigger: string;
  triggerType: 'questionnaire' | 'question';
  score: number;
}

export interface SpeakUpResponse {
  id: number;
  userId: number;
  message: string;
  seen: boolean;
  respondedAs: string;
  createdAt: string;
  responderFirstName: string;
  responderLastName: string;
}

export interface SpeakUp {
  questionnaireId: number;
  questionnaireName: string;
  speakUpId: number;
  speakUpMessage: string;
  shareIdentity: boolean;
  speakUpCreatedDate: string;
  userId: number | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  triggers: SpeakUpTrigger[];
  responses: SpeakUpResponse[];
}

export interface PaginationData {
  total: number;
  lastPage: number;
  prevPage: number | null;
  nextPage: number | null;
  perPage: number | string;
  currentPage: number;
  from: number;
  to: number;
}

export interface CompanySpeakUpsResponse {
  data: SpeakUp[];
  pagination: PaginationData;
}

export interface GetCompanyThoughtsPayload {
  responded: boolean;
}

export interface ThoughtResponseItem {
  id: number;
  thoughtsId: number;
  thoughtsResponseText: string;
  createdAt: string;
  updatedAt: string;
}

export interface CompanyThought {
  questionnaireId: number;
  questionnaireName: string;
  categoryName: string;
  thoughtId: number;
  question: string;
  score: number;
  shared: boolean;
  responded: boolean;
  responsesSeen: boolean;
  color: string;
  createdAt: string;
  updatedAt: string;
  responses: ThoughtResponseItem[];
  thoughtsText: string;
}

export interface CompanyThoughtsResponse {
  data: CompanyThought[];
  pagination: PaginationData;
}
